/* eslint no-constant-condition: "off" */
import { API_URL, apiClientConfig } from "api";
import type { UploadedFile } from "app/utils/uploadcare";
import { firebaseAuth, refreshFirebaseToken } from "$utils/firebase/client";
import { get } from "svelte/store";
import { isUserAuthed, session } from "$stores/auth";

function* processChunks(text: string) {
  let depth = 0;
  // allow curly braces when we are inside a string
  let allowCurlyBraces = false;
  let chunk: string[] = [];
  let previousChar = "";

  for (const c of text) {
    chunk.push(c);

    if (c === '"' && previousChar !== "\\") {
      allowCurlyBraces = !allowCurlyBraces;
    }

    previousChar = c;
    if (allowCurlyBraces) continue;

    if (c === "{") depth++;
    if (c === "}") depth--;

    if (depth === 0) {
      yield chunk.join("");
      chunk = [];
    }
  }

  // if this is hit, it will be an incomplete json chunk. Yield it so it can be handled
  if (chunk.length) {
    yield chunk.join("");
  }
}

function fetchChatStream(
  text: string,
  communitySlug: string,
  sessionId: string,
  files: UploadedFile[],
  agent: "copilot" | "customer" = "customer",
  copilotConfig: any = undefined,
  metadata: any = undefined
) {
  const queryParams = sessionId ? `?session_id=${sessionId}` : "";
  const authHeaders = apiClientConfig.apiKey ? { Authorization: apiClientConfig.apiKey } : {};
  // need to use fetch instead of axios because axios doesn't support streaming
  return fetch(
    `${API_URL}/api/community/communities/${communitySlug}/chat/handler/${queryParams}`,
    {
      method: "POST",
      // @ts-ignore
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        text,
        images: files.map((file) => file.src),
        agent,
        copilotConfig,
        metadata,
      }),
    }
  );
}

/**
 * Parses a stream of JSON objects that can be split apart.
 * Ie. the chat stream being parsed yields values like this:
 * - '{"property1": "value1", "other_prop": "other_value"}'
 * - '{"property3": "value3", "other_prop": "other_value"}'
 * - '{"property4": "value4", "other_prop": "other_value"}'
 * - '{"property5": "value5", "other_prop": "other_value"}'
 * - '{"property6": "value6", "other_prop": "other_value"}'
 *
 * Except it might actually return them split apart, or joined together, like this:
 * - '{"property1": "value1", "othe'
 * - 'r_prop": "other_value"}'
 * - '{"property3": "'
 * - 'value3", "other_prop": "other_value"}'
 * - '{"property4": "value4", "other_prop": "other_'
 * - 'value"}'
 * - '{"property5": "value5", "other_prop": "other_value"}{"property6": "value6", "other_prop": "other_value"}'
 *
 * @param reader
 */
export async function* parseStreamedJSON(reader: ReadableStreamDefaultReader) {
  const decoder = new TextDecoder();
  let error = null;
  let previousText = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const textValue = decoder.decode(value).trim();
    let textToParse = previousText + textValue;

    try {
      for (const chunk of processChunks(textToParse)) {
        yield JSON.parse(chunk);
        textToParse = textToParse.slice(chunk.length);
      }
      previousText = "";
      error = null;
    } catch (e) {
      // we try to recover from the error the next iteration by preprending previousText to the next chunk
      previousText = textToParse;
      error = e;
    }
  }

  // if there is still an error at the end, it wasn't possible to recover from it
  if (error) {
    console.log("Error parsing JSON for:\n", previousText);
    throw error;
  }
}

/**
 * yields objects of type:
 * { answer: string } | { metadata: { TRACE_ID: string, SESSION_ID: string } } | { event: "CONTEXT_READY" }
 *
 * @param text
 * @param sessionId
 */
export async function* streamResponse(
  text: string,
  files: UploadedFile[],
  communitySlug: string,
  sessionId: string,
  agent: "copilot" | "customer" = "customer",
  copilotConfig: any = undefined
) {
  if (!get(isUserAuthed)) {
    if (!(await session.widgetAuth(communitySlug, undefined, undefined))) {
      return;
    }
  }

  let response = await fetchChatStream(text, communitySlug, sessionId, files, agent, copilotConfig);
  if (response.status === 401) {
    await refreshFirebaseToken(firebaseAuth.currentUser!, apiClientConfig);
    response = await fetchChatStream(text, communitySlug, sessionId, files, agent, copilotConfig);
  }
  if (response.status !== 200) {
    return;
  }

  const reader = response.body!.getReader();
  yield* parseStreamedJSON(reader);
}
